<script setup lang="ts">
/**
 * Step Coordonnées du checkout adhésion :
 * formulaire payeur + navigation.
 */
import type { BuyerInfoModel } from "~/types/public-page";
import { isValidEmailStrict } from "@donoor/validators";
import { allerAuPremierChampEnErreur } from "~/utils/validationChamps";
import { messageTelephoneInvalide, telephoneValide } from "@donoor/utils";
import { usePaysAssociation } from "~/composables/usePaysAssociation";

const { t } = useI18n();

// Pays de l'association : un téléphone tapé sans indicatif s'y lit.
const paysAsso = usePaysAssociation();

const props = defineProps<{
  modelValue: BuyerInfoModel;
  hasParticipantsStep: boolean;
}>();

const modele = computed(() => props.modelValue);

const emit = defineEmits<{
  (e: 'update:modelValue', v: BuyerInfoModel): void;
  (e: 'back' | 'continue'): void;
}>();

// ── Validation à la demande ────────────────────────────────────────────────
// Bouton JAMAIS désactivé : le clic valide, cerne de rouge, explique, défile.
const erreurs = ref<Record<string, string>>({});
const aTente = ref(false);

function valider(): Record<string, string> {
  const b = modele.value;
  const e: Record<string, string> = {};
  if (!b.firstName?.trim()) e.firstName = t("adhesion.aslugStepCoordonnees.prenomObligatoire");
  if (!b.lastName?.trim()) e.lastName = t("adhesion.aslugStepCoordonnees.nomObligatoire");
  if (!b.email?.trim()) {
    e.email = t("adhesion.aslugStepCoordonnees.emailObligatoire");
  } else if (!isValidEmailStrict(b.email)) {
    e.email = t("adhesion.aslugStepCoordonnees.emailInvalideExemple");
  }
  if (!b.confirmEmail?.trim()) {
    e.confirmEmail = t("adhesion.aslugStepCoordonnees.confirmezEmail");
  } else if (b.email?.trim() !== b.confirmEmail?.trim()) {
    e.confirmEmail = t("adhesion.aslugStepCoordonnees.emailsNeCorrespondentPas");
  }
  // Lu dans le pays de l'association (règle partagée @donoor/utils).
  if (b.phone?.trim() && !telephoneValide(b.phone, paysAsso.value)) {
    e.phone = messageTelephoneInvalide(paysAsso.value);
  }
  return e;
}

function auClicContinuer() {
  aTente.value = true;
  const e = valider();
  erreurs.value = e;
  if (Object.keys(e).length > 0) {
    allerAuPremierChampEnErreur(e, "payeur");
    return;
  }
  emit("continue");
}

</script>

<template>
  <div class="p-3 md:p-5">
    <div class="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-800">
      <UIcon name="i-lucide-info" class="size-4 text-[#717198]" />
      <p class="text-xs text-[#717198] dark:text-gray-500">
        {{ $t('adhesion.aslugStepCoordonnees.champsObligatoiresIntro') }}<span class="text-red-500">*</span>{{ $t('adhesion.aslugStepCoordonnees.champsObligatoiresFin') }}
      </p>
    </div>

    <PublicPageBuyerInfoForm
      variant="a"
      :model-value="modelValue"
      :erreurs="aTente ? erreurs : undefined"
      prefixe-id="payeur"
      :title="hasParticipantsStep ? $t('adhesion.aslugStepCoordonnees.coordonneesPayeurTitre') : $t('adhesion.aslugStepCoordonnees.vosInformationsTitre')"
      :subtitle="hasParticipantsStep ? $t('adhesion.aslugStepCoordonnees.coordonneesPayeurSousTitre') : $t('adhesion.aslugStepCoordonnees.vosInformationsSousTitre')"
      @update:model-value="$emit('update:modelValue', $event)"
    />

    <!-- Navigation -->
    <div class="flex items-center justify-between mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
      <button
        class="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium text-[#505870] dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] transition-colors"
        @click="$emit('back')"
      >
        <UIcon name="i-lucide-arrow-left" class="size-4" />
        {{ $t('commun.actions.retour') }}
      </button>
      <button
        data-testid="btn-step3-continue"
        class="px-8 py-3 accent-btn rounded-xl text-sm font-semibold hover:bg-[#333] dark:hover:bg-gray-100 transition-colors"
        @click="auClicContinuer"
      >
        {{ $t('commun.actions.continuer') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Boutons d'action — couleur choisie dans la configuration de l'adhésion.
   Repli #212121 quand aucune couleur n'est posée. Même classe et mêmes
   variables que la billetterie (BslugStepRecap) : une seule règle pour les
   trois domaines. Karim 2026-09-12. */
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
</style>
